/**
 * TemplateSelect コンポーネント
 *
 * 保存済みのメッセージテンプレートを選択するためのコンポーネント。
 * テンプレート選択ボタンとモーダルを表示し、選択されたテンプレートを
 * 親コンポーネントに通知する。通常配信・セグメント配信で共通利用。
 *
 * @file テンプレート選択（共通コンポーネント）
 * @date 2025/09/18
 */

import React from 'react';
import { Button, Form } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { BroadcastTemplateModal } from '../../template';
import { MessageTemplate } from 'src/models/messageTemplate';

/**
 * Props インターフェース
 */
interface Props {
  /** テンプレート選択時のコールバック関数 */
  onTemplateSelect: (template: MessageTemplate) => void;
}

/**
 * TemplateSelect コンポーネント
 *
 * 配信テンプレート選択ボタンとモーダルを提供。
 * 選択されたテンプレートの内容を親コンポーネントのフォームに反映させる。
 *
 * @param props - コンポーネントのプロパティ
 * @returns JSX.Element
 */
export const TemplateSelect: React.FC<Props> = ({ onTemplateSelect }) => {
  // テンプレート選択モーダルの表示状態
  const [isTemplateModalOpen, setIsTemplateModalOpen] = React.useState(false);

  /**
   * テンプレート選択時の処理
   * 選択されたテンプレートを親コンポーネントに通知し、モーダルを閉じる
   *
   * @param template - 選択されたメッセージテンプレート
   */
  const handleSelectTemplate = (template: MessageTemplate) => {
    onTemplateSelect(template);
    setIsTemplateModalOpen(false);
  };

  return (
    <>
      <Form.Item
        label="テンプレート"
        tooltip="保存済みのテンプレートから選択できます"
      >
        <Button
          type="dashed"
          icon={<FileTextOutlined />}
          onClick={() => setIsTemplateModalOpen(true)}
          style={{ width: '100%' }}
        >
          配信テンプレートから選択
        </Button>
      </Form.Item>

      <BroadcastTemplateModal
        open={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        onSelect={handleSelectTemplate}
      />
    </>
  );
};
